import { HsMatrix3 } from '../objects/matrix3.class';
import type { HsMatrix4 } from '../objects/matrix4.class';
import { HsVector3 } from '../objects/vector3.class';

export class HsPlane {
  public readonly normal: HsVector3;
  public offset: number;

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #matrix3Temp1: HsMatrix3 = HsMatrix3.makeIdentityMatrix();

  constructor(x: number, y: number, z: number, offset: number) {
    this.normal = new HsVector3(x, y, z);
    this.offset = offset;
  }

  /**
   * Sets set.
   * @param normal normal.
   * @param offset offset.
   * @returns Result value.
   */
  public set(normal: HsVector3, offset: number): HsPlane {
    this.normal.copy(normal);
    this.offset = offset;
    return this;
  }

  /**
   * Copies copy.
   * @param other other.
   * @returns Result value.
   */
  public copy(other: HsPlane): HsPlane {
    this.normal.copy(other.normal);
    this.offset = other.offset;
    return this;
  }

  /**
   * Gets opposite vector.
   * @param targetPoint target point.
   * @returns Result value.
   */
  public getOppositeVector(targetPoint: HsVector3): HsVector3 {
    return targetPoint.copy(this.normal).multiplyByScalar(-this.offset);
  }

  /**
   * Handles apply matrix4.
   * @param matrix matrix.
   * @returns Result value.
   */
  public applyMatrix4(matrix: HsMatrix4): HsPlane {
    const normalMatrix: HsMatrix3 = HsPlane.#matrix3Temp1.setFromMatrix4(matrix.values);
    const referencePoint: HsVector3 = this.getOppositeVector(HsPlane.#vector3Temp1);
    referencePoint.applyMatrix4(matrix.values);
    const normal: HsVector3 = this.normal.applyMatrix3(normalMatrix.values).normalize();
    this.offset = -HsVector3.dot(referencePoint, normal);
    return this;
  }

  /**
   * Sets components.
   * @param x x.
   * @param y y.
   * @param z z.
   * @param offset offset.
   * @returns Result value.
   */
  public setComponents(x: number, y: number, z: number, offset: number): HsPlane {
    this.normal.set(x, y, z);
    this.offset = offset;
    return this;
  }

  /**
   * Handles normalize.
   * @returns Result value.
   */
  public normalize(): HsPlane {
    const reverseNormalLength: number = 1 / this.normal.length();
    this.normal.multiplyByScalar(reverseNormalLength);
    this.offset *= reverseNormalLength;
    return this;
  }

  /**
   * Handles negate.
   * @returns Result value.
   */
  public negate(): HsPlane {
    this.offset = -this.offset;
    this.normal.negate();
    return this;
  }

  /**
   * Builds plane xy.
   * @returns Result value.
   */
  public static makePlaneXY(): HsPlane {
    return new HsPlane(0, 0, 1, 0);
  }

  /**
   * Builds plane xz.
   * @returns Result value.
   */
  public static makePlaneXZ(): HsPlane {
    return new HsPlane(0, 1, 0, 0);
  }

  /**
   * Builds plane yz.
   * @returns Result value.
   */
  public static makePlaneYZ(): HsPlane {
    return new HsPlane(1, 0, 0, 0);
  }

  /**
   * Builds copy.
   * @returns Result value.
   */
  public static makeCopy(plane: HsPlane): HsPlane {
    return new HsPlane(plane.normal.x, plane.normal.y, plane.normal.z, plane.offset);
  }

  /**
   * Handles distance.
   * @param point point.
   * @returns Result value.
   */
  public static distance(plane: HsPlane, point: HsVector3): number {
    return HsVector3.dot(plane.normal, point) + plane.offset;
  }

  /**
   * Handles cast ray.
   * @param rayOrigin ray origin.
   * @param rayDirection ray direction.
   * @param targetPoint target point.
   * @returns Result value.
   */
  public static castRay(
    plane: HsPlane,
    rayOrigin: HsVector3,
    rayDirection: HsVector3,
    targetPoint: HsVector3
  ): HsVector3 {
    const dotDirection: number = HsVector3.dot(plane.normal, rayDirection);
    if (dotDirection === 0) {
      return targetPoint.set(0, 0, 0);
    }
    const dotOrigin: number = -(HsVector3.dot(rayOrigin, plane.normal) + plane.offset);
    const distanceToPlane: number = dotOrigin / dotDirection;
    return targetPoint.copy(rayDirection).multiplyByScalar(distanceToPlane).add(rayOrigin);
  }
}
